"use client";

import type { CSSProperties } from "react";
import { useEffect, useId, useState } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa6";
import TextBlock from "./TextBlock";

export type GhostExpandableTextBreakpoint = "sm" | "md" | "lg" | "xl" | "2xl";

interface GhostExpandableTextProps {
  text: string;
  lineClamp?: number;
  tone?: "default" | "subtle" | "strong" | "muted" | "soft";
  className?: string;
  disableClampAt?: GhostExpandableTextBreakpoint;
  desktopSize?: "default" | "grow";
}

const breakpointQueries: Record<GhostExpandableTextBreakpoint, string> = {
  sm: "(min-width: 640px)",
  md: "(min-width: 768px)",
  lg: "(min-width: 1024px)",
  xl: "(min-width: 1280px)",
  "2xl": "(min-width: 1536px)",
};

export default function GhostExpandableText({
  text,
  lineClamp = 2,
  tone = "default",
  className,
  disableClampAt,
  desktopSize = "default",
}: GhostExpandableTextProps) {
  const [expanded, setExpanded] = useState(false);
  const [isClampDisabled, setIsClampDisabled] = useState(false);
  const descriptionId = useId();

  useEffect(() => {
    if (!disableClampAt) {
      setIsClampDisabled(false);
      return;
    }

    const mediaQueryList = window.matchMedia(breakpointQueries[disableClampAt]);
    const updateClampState = () => {
      setIsClampDisabled(mediaQueryList.matches);
    };

    updateClampState();
    mediaQueryList.addEventListener("change", updateClampState);

    return () => {
      mediaQueryList.removeEventListener("change", updateClampState);
    };
  }, [disableClampAt]);

  const isCollapsed = !expanded && !isClampDisabled;
  const collapsedParagraphStyle: CSSProperties | undefined = isCollapsed
    ? {
        display: "-webkit-box",
        WebkitLineClamp: lineClamp,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      }
    : undefined;

  return (
    <div>
      <TextBlock
        as="p"
        variant="small"
        tone={tone}
        desktopSize={desktopSize}
        className={className}
        style={collapsedParagraphStyle}
        id={descriptionId}
      >
        {text}
      </TextBlock>

      {isClampDisabled ? null : (
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={descriptionId}
          onClick={() => setExpanded((current) => !current)}
          className="mt-2 inline-flex items-center gap-1 text-sm font-semibold uppercase tracking-[0.14em] text-ghost-gold transition-colors hover:text-white"
        >
          <span>{expanded ? "Read Less" : "Read More"}</span>
          <span aria-hidden="true">
            {expanded ? (
              <FaChevronUp size={14} aria-hidden="true" />
            ) : (
              <FaChevronDown size={14} aria-hidden="true" />
            )}
          </span>
        </button>
      )}
    </div>
  );
}
